import analyticstracker from "@jmc/analyticstracker";
import { BaseImpression } from "@jmc/core/src/components/BaseImpression/BaseImpression";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { useEventListener } from "@jmc/utils/hooks/useEventListener";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import React, { useEffect, useRef, useState } from "react";

export interface PropTypes {
    children: JSX.Element | JSX.Element[];
    name: string;
    type?: EventTypes;
    checkVisibility?: boolean;
    trackRepeatedly?: boolean;
}

export const NavigationImpression: React.FunctionComponent<PropTypes> = (props: PropTypes) => {
    const { jnjFullBranded } = useJnjBranding();
    const {
        children,
        name,
        type = EventTypes.NAVIGATION_IMPRESSION,
        checkVisibility = true,
        trackRepeatedly = false,
    } = props;
    const navigationContainer = useRef<HTMLDivElement>(null);
    const childrenRef = useRef<HTMLElement>(null);
    const [hasDirectChildFixedPosition, setHasDirectChildFixedPosition] = useState(false);
    const defaultHeight = "100%";

    // Copy position styles from children to container
    //this is needed because we had a case where the children was position fixed and then the analyticsTracker was not able to track if the element was visible or not because was out of scope of the NavigationImpression container
    useEffect(() => {
        let positionStyles;
        // Check if any child has position fixed first
        if (navigationContainer.current) {
            const directChildren = navigationContainer.current.children;
            for (let i = 0; i < directChildren.length; i++) {
                const child = directChildren[i] as HTMLElement;
                const childStyle = window.getComputedStyle(child);
                if (childStyle.position === "fixed") {
                    setHasDirectChildFixedPosition(true);
                    break;
                }
            }
        }

        if (childrenRef.current && navigationContainer.current && hasDirectChildFixedPosition) {
            const computedStyle = window.getComputedStyle(childrenRef.current);

            // Calculate total height of direct children using reduce
            const directChildren = Array.from(navigationContainer.current.children) as HTMLElement[];
            const totalHeight = directChildren.reduce((sum, child) => {
                const childStyle = window.getComputedStyle(child);
                const childHeight = parseFloat(childStyle.height) || child.offsetHeight || 0;
                return sum + childHeight;
            }, 0);

            positionStyles = {
                position: computedStyle.position,
                top: computedStyle.top,
                right: computedStyle.right,
                bottom: computedStyle.bottom,
                left: computedStyle.left,
                minHeight: "1px",
                height: totalHeight > 0 ? `${totalHeight}px` : defaultHeight,
                zIndex: computedStyle.zIndex,
            };
        } else {
            positionStyles = {
                height: defaultHeight,
            };
        }
        Object.assign(navigationContainer.current.style, positionStyles);
    }, [children, jnjFullBranded, hasDirectChildFixedPosition]);

    // track clicks registering the id of the clicked element
    useEventListener(
        "click",
        (e: MouseEvent) => {
            // Do not track clicks on input fields. E.g. searcher
            if ((e.target as HTMLElement)?.tagName === "INPUT") {
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const path = (e as any)?.composedPath() || [];

            // keep the clicks within this navigation container
            for (let x = 0; x < path.length; x++) {
                const step = path[x];
                const container = step?.dataset?.trackingNavigationContainer;
                if (container && container === name) {
                    // if the first found container is this one then we continue
                    break;
                } else if (container && container !== name) {
                    // if the first found container is not this one then we exit
                    return;
                }
            }

            // look for the first id in  the click path to use as linkname
            let linkname: string = undefined;
            for (let x = 0; x < path.length; x++) {
                const step = path[x];

                // don't look outside of the navigation container
                if (step === navigationContainer.current) {
                    break;
                }
                // return the first found id
                if (step?.getAttribute("id") !== null) {
                    linkname = step.getAttribute("id");
                    break;
                }
                // return data linkname if a data-linkname attribute is present
                if (step?.getAttribute("data-linkname") !== null) {
                    linkname = step.getAttribute("data-linkname");
                    break;
                }
            }

            // Adding level if a data-level attribute is present
            const level = (e.target as Element)?.closest("li")?.getAttribute("data-level");
            if (level && linkname) linkname = `level${level}.${linkname}`;

            // adding the click type based on user clicks
            let clickType = "mouse";
            if (e?.clientX === 0 && e?.clientY === 0) clickType = "enter";

            // only if an id is found with the click path we track it
            if (linkname) {
                analyticstracker().trackInteraction(e, {
                    changeEvent: EventTypes.NAVIGATION_CLICK,
                    addData: { info: { linkname, click_type: clickType, name } },
                });
            }
        },
        navigationContainer,
    );
    const getClonedChildren = (): JSX.Element | JSX.Element[] =>
        Array.isArray(children)
            ? children
                  .filter((child) => child !== null && child !== undefined)
                  .map((child, index) =>
                      React.cloneElement(child as React.ReactElement, {
                          key: index,
                          ref: index === 0 ? childrenRef : undefined,
                      }),
                  )
            : children && React.cloneElement(children as React.ReactElement, { ref: childrenRef });

    return (
        <BaseImpression
            eventType={type}
            trackingInfo={{ name }}
            checkVisibility={checkVisibility}
            containerRef={navigationContainer}
            trackRepeatedly={trackRepeatedly}
            trackingOptions={{ checkVisibility: checkVisibility }}
            containerProps={
                {
                    ["data-tracking-navigation-container"]: name,
                    role: "none",
                } as React.HTMLAttributes<HTMLDivElement>
            }
        >
            {hasDirectChildFixedPosition ? getClonedChildren() : children}
        </BaseImpression>
    );
};
